import React from 'react';
import { Link } from 'react-router-dom';

const Home: React.FC = () => {
  return (
    <main>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-inner">
            <div className="hero-content">
              <div className="hero-badge">
                <span className="hero-badge-dot"></span>
                Your External Data Team
              </div>
              <h1>
                Turn your data into <span className="hero-highlight">clear decisions</span>
              </h1>
              <p>
                We help growing companies (10–250 employees) operate like mid-sized ones — 
                setting up the data systems, analytics, and automation you need, without building an in-house team.
              </p>
              <div className="hero-ctas">
                <Link to="/contact" className="btn btn-primary btn-lg">
                  Schedule a Call
                </Link>
                <Link to="/services" className="btn btn-secondary btn-lg">
                  View Services
                </Link>
              </div>
            </div>
            <div className="hero-visual">
              <div className="hero-image">
                <div className="hero-chart">
                  <div className="hero-chart-line"></div>
                  <div className="hero-chart-dots">
                    <div className="hero-chart-dot"></div>
                    <div className="hero-chart-dot"></div>
                    <div className="hero-chart-dot"></div>
                    <div className="hero-chart-dot"></div>
                    <div className="hero-chart-dot"></div>
                    <div className="hero-chart-dot"></div>
                    <div className="hero-chart-dot"></div>
                  </div>
                </div>
                <div className="hero-stats">
                  <div className="hero-stat">
                    <div className="hero-stat-value">85%</div>
                    <div className="hero-stat-label">Less Manual Work</div>
                  </div>
                  <div className="hero-stat">
                    <div className="hero-stat-value">3x</div>
                    <div className="hero-stat-label">Faster Decisions</div>
                  </div>
                  <div className="hero-stat">
                    <div className="hero-stat-value">1</div>
                    <div className="hero-stat-label">Source of Truth</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props - Reimagined */}
      <section className="section value-props-v2">
        <div className="container">
          <div className="value-props-v2-header">
            <span className="value-props-v2-label">The Outcomes</span>
            <h2>What we help you achieve</h2>
            <p>
              We focus on outcomes that matter to growing businesses — not technical complexity.
            </p>
          </div>
          
          <div className="value-props-v2-grid">
            <div className="value-card" data-index="01">
              <div className="value-card-inner">
                <div className="value-card-index">01</div>
                <div className="value-card-line"></div>
                <div className="value-card-content">
                  <h3>Clarity</h3>
                  <p>Know what's happening in your business without digging through spreadsheets.</p>
                </div>
                <div className="value-card-shape">
                  <svg viewBox="0 0 80 80" fill="none">
                    <circle cx="40" cy="40" r="30" strokeWidth="2" className="shape-stroke"/>
                    <circle cx="40" cy="40" r="20" strokeWidth="2" className="shape-stroke-inner"/>
                    <circle cx="40" cy="40" r="6" className="shape-fill"/>
                  </svg>
                </div>
              </div>
            </div>

            <div className="value-card" data-index="02">
              <div className="value-card-inner">
                <div className="value-card-index">02</div>
                <div className="value-card-line"></div>
                <div className="value-card-content">
                  <h3>Reliability</h3>
                  <p>One source of truth you can actually trust for decisions.</p>
                </div>
                <div className="value-card-shape">
                  <svg viewBox="0 0 80 80" fill="none">
                    <rect x="15" y="15" width="50" height="50" strokeWidth="2" className="shape-stroke" rx="4"/>
                    <rect x="25" y="25" width="30" height="30" strokeWidth="2" className="shape-stroke-inner" rx="2"/>
                    <rect x="35" y="35" width="10" height="10" className="shape-fill"/>
                  </svg>
                </div>
              </div>
            </div>

            <div className="value-card" data-index="03">
              <div className="value-card-inner">
                <div className="value-card-index">03</div>
                <div className="value-card-line"></div>
                <div className="value-card-content">
                  <h3>Leverage</h3>
                  <p>Automate repetitive work so your team can focus on what matters.</p>
                </div>
                <div className="value-card-shape">
                  <svg viewBox="0 0 80 80" fill="none">
                    <polygon points="40,10 70,65 10,65" strokeWidth="2" className="shape-stroke"/>
                    <polygon points="40,25 55,55 25,55" strokeWidth="2" className="shape-stroke-inner"/>
                    <circle cx="40" cy="45" r="5" className="shape-fill"/>
                  </svg>
                </div>
              </div>
            </div>

            <div className="value-card" data-index="04">
              <div className="value-card-inner">
                <div className="value-card-index">04</div>
                <div className="value-card-line"></div>
                <div className="value-card-content">
                  <h3>Confidence</h3>
                  <p>Make decisions backed by data, not guesswork.</p>
                </div>
                <div className="value-card-shape">
                  <svg viewBox="0 0 80 80" fill="none">
                    <polygon points="40,10 65,30 55,60 25,60 15,30" strokeWidth="2" className="shape-stroke"/>
                    <polygon points="40,22 52,35 47,52 33,52 28,35" strokeWidth="2" className="shape-stroke-inner"/>
                    <circle cx="40" cy="40" r="5" className="shape-fill"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          <div className="value-props-v2-connector">
            <div className="connector-line"></div>
            <div className="connector-pulse"></div>
          </div>
        </div>
      </section>

      {/* Services Preview - Data Analytics Theme */}
      <section className="section services-v2">
        {/* Background Data Visualizations */}
        <div className="services-v2-bg">
          {/* Animated line chart */}
          <svg className="bg-chart bg-chart-1" viewBox="0 0 200 100" preserveAspectRatio="none">
            <polyline className="chart-line" points="0,80 30,65 60,70 90,45 120,50 150,30 180,35 200,20" />
            <polyline className="chart-fill" points="0,80 30,65 60,70 90,45 120,50 150,30 180,35 200,20 200,100 0,100" />
          </svg>
          
          {/* Animated bar chart */}
          <div className="bg-bars">
            <div className="bg-bar" style={{ '--height': '40%', '--delay': '0s' } as React.CSSProperties}></div>
            <div className="bg-bar" style={{ '--height': '65%', '--delay': '0.1s' } as React.CSSProperties}></div>
            <div className="bg-bar" style={{ '--height': '45%', '--delay': '0.2s' } as React.CSSProperties}></div>
            <div className="bg-bar" style={{ '--height': '80%', '--delay': '0.3s' } as React.CSSProperties}></div>
            <div className="bg-bar" style={{ '--height': '55%', '--delay': '0.4s' } as React.CSSProperties}></div>
            <div className="bg-bar" style={{ '--height': '70%', '--delay': '0.5s' } as React.CSSProperties}></div>
          </div>
          
          {/* Floating data points */}
          <div className="bg-dots">
            <div className="bg-dot" style={{ '--x': '15%', '--y': '25%', '--delay': '0s' } as React.CSSProperties}></div>
            <div className="bg-dot" style={{ '--x': '85%', '--y': '35%', '--delay': '0.5s' } as React.CSSProperties}></div>
            <div className="bg-dot" style={{ '--x': '25%', '--y': '70%', '--delay': '1s' } as React.CSSProperties}></div>
            <div className="bg-dot" style={{ '--x': '75%', '--y': '80%', '--delay': '1.5s' } as React.CSSProperties}></div>
            <div className="bg-dot" style={{ '--x': '50%', '--y': '20%', '--delay': '2s' } as React.CSSProperties}></div>
          </div>
          
          {/* Pie chart segment */}
          <svg className="bg-pie" viewBox="0 0 100 100">
            <circle className="pie-track" cx="50" cy="50" r="40" />
            <circle className="pie-segment" cx="50" cy="50" r="40" />
          </svg>
          
          {/* Grid lines */}
          <div className="bg-grid"></div>
        </div>
        
        <div className="container services-v2-content">
          <div className="services-v2-header">
            <span className="services-v2-label">Our Services</span>
            <h2>How we help</h2>
            <p>
              From building your data foundation to delivering actionable insights — 
              we provide everything a growing company needs to operate with better data.
            </p>
          </div>
          
          <div className="services-v2-grid">
            <div className="service-v2-card" data-service="foundation">
              <div className="service-v2-visual">
                <svg viewBox="0 0 60 60" fill="none">
                  <rect x="8" y="35" width="12" height="20" className="bar bar-1"/>
                  <rect x="24" y="25" width="12" height="30" className="bar bar-2"/>
                  <rect x="40" y="15" width="12" height="40" className="bar bar-3"/>
                  <line x1="5" y1="55" x2="55" y2="55" className="baseline"/>
                </svg>
              </div>
              <div className="service-v2-content">
                <h3>Data Foundation</h3>
                <p>
                  Get your data organized and reliable. We build the foundation most growing 
                  companies never have time to set up.
                </p>
                <Link to="/services#foundation" className="service-v2-link">
                  Learn more <span>→</span>
                </Link>
              </div>
            </div>

            <div className="service-v2-card" data-service="visibility">
              <div className="service-v2-visual">
                <svg viewBox="0 0 60 60" fill="none">
                  <polyline className="line-chart" points="5,45 15,35 25,40 35,25 45,30 55,15"/>
                  <circle cx="5" cy="45" r="3" className="data-point dp-1"/>
                  <circle cx="25" cy="40" r="3" className="data-point dp-2"/>
                  <circle cx="45" cy="30" r="3" className="data-point dp-3"/>
                  <circle cx="55" cy="15" r="3" className="data-point dp-4"/>
                </svg>
              </div>
              <div className="service-v2-content">
                <h3>Dashboards & Visibility</h3>
                <p>
                  Clear dashboards built around your business — not generic templates. 
                  Know what's happening without digging.
                </p>
                <Link to="/services#visibility" className="service-v2-link">
                  Learn more <span>→</span>
                </Link>
              </div>
            </div>

            <div className="service-v2-card" data-service="forecasting">
              <div className="service-v2-visual">
                <svg viewBox="0 0 60 60" fill="none">
                  <polyline className="trend-line" points="5,50 20,40 35,35 50,20"/>
                  <polyline className="forecast-line" points="35,35 45,28 55,22"/>
                  <line x1="35" y1="10" x2="35" y2="55" className="divider"/>
                  <text x="20" y="58" className="chart-label">Past</text>
                  <text x="42" y="58" className="chart-label">Future</text>
                </svg>
              </div>
              <div className="service-v2-content">
                <h3>Forecasting & Planning</h3>
                <p>
                  Turn historical data into realistic forecasts you can plan around. 
                  Better hiring and budgeting decisions.
                </p>
                <Link to="/services#forecasting" className="service-v2-link">
                  Learn more <span>→</span>
                </Link>
              </div>
            </div>
          </div>
          
          <div className="services-v2-cta">
            <Link to="/services" className="btn btn-navy">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Preview */}
      <section className="section how-section">
        <div className="container">
          <div className="how-header">
            <div className="accent-bar accent-bar-center mb-lg"></div>
            <h2>How it works</h2>
            <p>
              A straightforward process that gets you from scattered data to clear direction.
            </p>
          </div>
          <div className="how-steps">
            <div className="how-step">
              <div className="how-step-number">1</div>
              <h3>Discovery Call</h3>
              <p>We learn about your business, data sources, and goals.</p>
            </div>
            <div className="how-step">
              <div className="how-step-number">2</div>
              <h3>Assessment</h3>
              <p>We map your current systems and identify opportunities.</p>
            </div>
            <div className="how-step">
              <div className="how-step-number">3</div>
              <h3>Implementation</h3>
              <p>We build and deploy your data infrastructure.</p>
            </div>
            <div className="how-step">
              <div className="how-step-number">4</div>
              <h3>Ongoing Support</h3>
              <p>We maintain and improve as your business grows.</p>
            </div>
          </div>
          <div className="text-center mt-2xl">
            <Link to="/how-it-works" className="btn btn-secondary">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-inner">
            <h2>Ready to turn your data into an advantage?</h2>
            <p>
              Schedule a free discovery call. We'll learn about your business and show you 
              what's possible — no commitment required.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-green btn-lg">
                Schedule a Call
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
